import type { SaleRecord } from "../shared/commercial";
import type { VehicleListItem } from "../shared/types";

export const vehicleGuidedActions = ["purchase", "cost", "document", "photos"] as const;
export type VehicleGuidedAction = (typeof vehicleGuidedActions)[number];

export const saleGuidedActions = ["invoice", "delivery"] as const;
export type SaleGuidedAction = (typeof saleGuidedActions)[number];

type GuidedActionCopy = {
  title: string;
  description: string;
  emptyTitle: string;
  emptyDescription: string;
};

export const vehicleGuidedActionCopy: Record<VehicleGuidedAction, GuidedActionCopy> = {
  photos: {
    title: "¿Qué vehículo quieres fotografiar?",
    description: "Elige un vehículo para abrir su galería, subir fotos o mejorar las que ya tiene.",
    emptyTitle: "No encontramos ese vehículo",
    emptyDescription: "Prueba con otra matrícula, marca o modelo, o añade un vehículo para empezar su galería.",
  },
  purchase: {
    title: "¿Qué vehículo acabas de comprar?",
    description: "Mostramos los borradores de stock propio. Abre uno para registrar su proveedor, importe y fiscalidad.",
    emptyTitle: "No hay compras pendientes",
    emptyDescription: "Da de alta el vehículo como stock propio y después podrás registrar aquí su compra.",
  },
  cost: {
    title: "¿A qué vehículo corresponde el coste?",
    description: "Busca el vehículo y lo abriremos directamente en el formulario de costes.",
    emptyTitle: "No encontramos ese vehículo",
    emptyDescription: "Prueba con otra matrícula, VIN, marca o modelo, o vuelve al listado completo.",
  },
  document: {
    title: "¿En qué vehículo quieres archivar el documento?",
    description: "Elige el vehículo y abriremos directamente su carpeta documental.",
    emptyTitle: "No encontramos ese vehículo",
    emptyDescription: "Prueba con otra matrícula, VIN, marca o modelo, o vuelve al listado completo.",
  },
};

export const saleGuidedActionCopy: Record<SaleGuidedAction, GuidedActionCopy> = {
  invoice: {
    title: "¿Qué venta quieres facturar?",
    description: "Mostramos únicamente las ventas con el contrato completado y listas para emitir factura.",
    emptyTitle: "No hay ventas listas para facturar",
    emptyDescription: "Cuando una venta tenga el contrato completado, aparecerá aquí automáticamente.",
  },
  delivery: {
    title: "¿Qué vehículo vas a entregar?",
    description: "Mostramos únicamente las ventas facturadas. Al abrir una irás al checklist y al acta de entrega.",
    emptyTitle: "No hay entregas preparadas",
    emptyDescription: "Las ventas aparecerán aquí después de emitir su factura.",
  },
};

export function parseVehicleGuidedAction(value: string | null): VehicleGuidedAction | null {
  return vehicleGuidedActions.find((action) => action === value) ?? null;
}

export function parseSaleGuidedAction(value: string | null): SaleGuidedAction | null {
  return saleGuidedActions.find((action) => action === value) ?? null;
}

export function vehicleGuidedListUrl(action: VehicleGuidedAction): string {
  return `/app/stock?intent=${action}`;
}

export function saleGuidedListUrl(action: SaleGuidedAction): string {
  return `/app/leads?view=ventas&intent=${action}`;
}

export function isVehicleEligibleForGuidedAction(
  action: VehicleGuidedAction,
  vehicle: Pick<VehicleListItem, "status" | "acquisitionMode">,
): boolean {
  if (action === "purchase") return vehicle.status === "draft" && vehicle.acquisitionMode === "owned";
  return true;
}

export function isSaleEligibleForGuidedAction(
  action: SaleGuidedAction,
  sale: Pick<SaleRecord, "status">,
): boolean {
  return action === "invoice" ? sale.status === "contracted" : sale.status === "invoiced";
}

export function vehicleGuidedDetailTab(action: VehicleGuidedAction): "operations" | "expenses" | "documents" | "photos" {
  return action === "purchase" ? "operations" : action === "cost" ? "expenses" : action === "photos" ? "photos" : "documents";
}

export function vehicleGuidedDestination(action: VehicleGuidedAction, vehicleId: string): string {
  return `/app/stock/${encodeURIComponent(vehicleId)}?tab=${vehicleGuidedDetailTab(action)}&action=${action}&fromIntent=${action}`;
}

export function saleGuidedDestination(action: SaleGuidedAction, saleId: string): string {
  const hash = action === "delivery" ? "#entrega" : "";
  return `/app/ventas/${encodeURIComponent(saleId)}?action=${action}&fromIntent=${action}${hash}`;
}
